"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useSyncExternalStore } from "react";
import { useAuthStore } from "@/store/useAuthStore";

/**
 * Session-scoped truth for "did *this* user like / watchlist this entity",
 * shared by every mounted Like/Watchlist control for the same model.
 *
 * Why this exists rather than plain useState inside the toggle hooks:
 *
 * 1. The same entity is routinely rendered by several independent controls at
 *    once — the collection detail page mounts LikeButton/WatchlistButton twice
 *    (desktop + mobile) plus a separate LikeCount, the artist page does the
 *    same, and a list can show a card while a rail below shows the same item.
 *    With per-component state, toggling one left the others stale (the reason
 *    a `window.dispatchEvent("collection:like-changed")` bus had grown inside
 *    CollectionDetailClient — this replaces it).
 * 2. `current_user` arriving late — or arriving *wrong* — must not undo a
 *    user's action. Several lists are seeded from an anonymous SSR payload
 *    (public ISR pages can't read the token cookie without opting out of
 *    static rendering), so a refetch or a re-render can hand a control
 *    `current_user.like === false` for something the user just liked. Props
 *    are only the first-paint default here; once the user has acted, this
 *    store wins.
 * 3. Rapid/duplicate clicks are deduped per (model, action) across *all*
 *    instances, not just within one component, so two cards for the same NFT
 *    can't fire two conflicting requests.
 *
 * Deliberately not the React Query cache: query keys and payload shapes differ
 * per feature (["collections", …] vs ["collections-endpoint", …] vs plain SSR
 * props), and an id alone can't tell an nft from a blog, so a generic cache
 * walk would mispatch. Keying by `modelType:modelId` cannot.
 */

export type InteractionState = {
  /** Optimistic `current_user.like`, held only while it's still newer than what
   *  the server is reporting (see `likeBase`). */
  like?: boolean;
  /** The `initialLiked` prop that `like` was flipped away from. While the server
   *  keeps reporting this same value it is stale (or anonymous) and the
   *  optimistic value wins; the moment it reports anything else, the server is
   *  ahead of us and wins instead. This is what stops an optimistic flip from
   *  outliving its usefulness for the rest of the session. */
  likeBase?: boolean;
  /** Displayed like count that goes with `like`. */
  likeCount?: number;
  /** Same idea as `likeBase`, for the count. */
  likeCountBase?: number;
  /** Optimistic `current_user.watchlist`; see `like`. */
  watchlist?: boolean;
  /** Same idea as `likeBase`, for the watchlist flag. */
  watchlistBase?: boolean;
  likePending?: boolean;
  watchlistPending?: boolean;
};
const states = new Map<string, InteractionState>();
const listeners = new Map<string, Set<() => void>>();

/** `null` for an unusable id, which callers treat as "not addressable yet". */
export function interactionKey(modelType: string, modelId?: number | string | null): string | null {
  if (modelId == null || modelId === "" || modelId === 0) return null;
  return `${modelType}:${modelId}`;
}
export function getInteraction(key: string | null): InteractionState | undefined {
  return key ? states.get(key) : undefined;
}
export function setInteraction(key: string, patch: InteractionState): void {
  states.set(key, {
    ...states.get(key),
    ...patch
  });
  listeners.get(key)?.forEach(listener => listener());
}

/** Restores a whole entry — used to roll a failed mutation back. */
export function replaceInteraction(key: string, next: InteractionState | undefined): void {
  if (next === undefined) states.delete(key);else states.set(key, next);
  listeners.get(key)?.forEach(listener => listener());
}
function clearInteractions(): void {
  const keys = [...states.keys()];
  states.clear();
  keys.forEach(key => listeners.get(key)?.forEach(listener => listener()));
}

// A different user must never inherit the previous one's like/watchlist state.
useAuthStore.subscribe((state, previous) => {
  if (state.user?.id !== previous.user?.id) clearInteractions();
});
function makeSubscribe(key: string | null) {
  return (onChange: () => void) => {
    if (!key) return () => {};
    let set = listeners.get(key);
    if (!set) {
      set = new Set();
      listeners.set(key, set);
    }
    set.add(onChange);
    return () => {
      set!.delete(onChange);
      if (set!.size === 0) listeners.delete(key);
    };
  };
}
export function useInteraction(key) {
  const $ = _c(4);
  let t0;
  if ($[0] !== key) {
    t0 = makeSubscribe(key);
    $[0] = key;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const subscribe = t0;
  let t1;
  if ($[2] !== key) {
    t1 = () => getInteraction(key);
    $[2] = key;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useSyncExternalStore(subscribe, t1, _temp);
}
function _temp() {}